import { useState, useEffect } from "react";
import get from "lodash/get";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import colors from "theme/colors";
import { StyledTextField } from "@/atoms/TextField/styles";
import CheckoutPurchaseButtonWithTotal from "components/checkout/CheckoutPurchaseButtonWithTotal";
import PropTypes from "prop-types";
import cx from "classnames";
import analytics from "analytics";

/**
 * Payment form that encapsulates our default stripe payment method.
 *
 * @param {Object} props
 * @param {String} props.orderTotal
 * @param {String} [props.paymentButtonLabel]
 * @param {String} props.fieldLabel
 * @param {String} props.error
 * @param {Function} props.onToken
 * @param {Function} props.onError
 * @param {Boolean} [props.inline]
 * @param {Function} [props.onSubmittingStateChange]
 * @param {Function} props.onSubmitDisabledToggle
 * @param {Boolean} props.isGuidedSignUpFunnel
 */
const CheckoutStripeForm = (props) => {
  const [cardElement, setCardElement] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [onChangeTracked, setOnChangeTracked] = useState(false);

  const onChange = (element) => {
    if (!onChangeTracked) {
      // for tracking that the stripe elements are loading correctly for end users
      analytics.trackAmplitudeClickEvent("stripe_form_interacted");
      setOnChangeTracked(true);
    }

    setCardElement(element);
  };

  const elementError = get(cardElement, "error.message");
  const showPropsError = !props.error?.includes("PayPal");
  const error = elementError || (showPropsError && props.error);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    props.onSubmittingStateChange?.(submitting);
  }, [submitting]);

  useEffect(() => {
    const disabled = !cardElement.complete || Boolean(elementError);
    setSubmitDisabled(disabled);
    props.onSubmitDisabledToggle?.(disabled);
  }, [cardElement?.complete, elementError]);

  useEffect(() => {
    if (stripe) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Demo total",
          amount: 995,
        },
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          pr.on("token", async ({ token }, complete) => {
            await props.onToken({ token: token.id });
            complete();
          });
        }
      });
    }
  }, [stripe]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !stripe || !elements) {
      return null;
    }

    setSubmitting(true);

    const cardElement = elements.getElement(CardElement);

    stripe
      .createToken(cardElement)
      .then(async ({ token, error: tokenError }) => {
        if (tokenError) {
          // see https://stripe.com/docs/api/errors
          let errorMessage =
            "There was a problem processing your payment. Please refresh and try again.";
          if (tokenError?.message && tokenError?.type === "card_error") {
            errorMessage = tokenError?.message;
          } else {
            console.error(
              "Error returned from `stripe.createToken` did not contain an error message or was not a card error: ",
              tokenError
            );
          }

          await props.onError?.(errorMessage);
        } else {
          await props.onToken({ token: token.id });
        }
        setSubmitting(false);
      });
  };

  return (
    <form className={props.className} id="stripe-form" onSubmit={handleSubmit}>
      <div className={"field"}>
        <label className={cx("text-sm", { "text-error": error })}>
          {props.fieldLabel}
        </label>
        <StyledTextField className={"stripe-container grid items-center"}>
          <CardElement
            onChange={onChange}
            options={{
              style: {
                invalid: {
                  color: colors.error,
                },
              },
            }}
          />
        </StyledTextField>
        <div className={"text-sm text-error mt-1"}>{error}</div>
      </div>
      {!props.isGuidedSignUpFunnel && (
        <div className={"btn-wrapper"}>
          <CheckoutPurchaseButtonWithTotal
            onSubmit={handleSubmit}
            orderTotal={props.orderTotal}
            paymentButtonLabel={props.paymentButtonLabel}
            disabled={submitDisabled}
            dataTestId={"submit-stripe"}
            loading={submitting}
          />
        </div>
      )}
    </form>
  );
};

CheckoutStripeForm.propTypes = {
  orderTotal: PropTypes.string,
  paymentButtonLabel: PropTypes.string,
  fieldLabel: PropTypes.string,
  error: PropTypes.string,
  onToken: PropTypes.func,
  onError: PropTypes.func.isRequired,
  inline: PropTypes.bool,
  onSubmittingStateChange: PropTypes.func,
  onSubmitDisabledToggle: PropTypes.func,
  isGuidedSignUpFunnel: PropTypes.bool,
};

CheckoutStripeForm.defaultProps = {
  fieldLabel: "Card Details",
};

export default CheckoutStripeForm;
